import type { OnboardingStep, UserRole } from './constants'

export interface StepDefinition {
  id: OnboardingStep
  title: string
  description: string
  isHardGate: boolean
  requiredRoles: UserRole[]
}

const ALL_STEPS: StepDefinition[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    description: 'Introduction to LUKHAS Team',
    isHardGate: false,
    requiredRoles: ['staff', 'manager', 'admin', 'contractor'],
  },
  {
    id: 'legal',
    title: 'Legal & Compliance',
    description: 'Review and sign required documents',
    isHardGate: true,
    requiredRoles: ['staff', 'manager', 'admin', 'contractor'],
  },
  {
    id: 'security',
    title: 'Security Training',
    description: 'Complete security awareness training',
    isHardGate: true,
    requiredRoles: ['staff', 'manager', 'admin'],
  },
  {
    id: 'system-education',
    title: 'System Education',
    description: 'Learn about our systems and tools',
    isHardGate: false,
    requiredRoles: ['staff', 'manager', 'admin'],
  },
  {
    id: 'dashboard-tour',
    title: 'Dashboard Tour',
    description: 'Get familiar with your workspace',
    isHardGate: false,
    requiredRoles: ['staff', 'manager', 'admin', 'contractor'],
  },
  {
    id: 'certification',
    title: 'Certification',
    description: 'Complete your onboarding certification',
    isHardGate: true,
    requiredRoles: ['staff', 'manager', 'admin'],
  },
]

export function getStepsForRole(role: UserRole): StepDefinition[] {
  return ALL_STEPS.filter((step) => step.requiredRoles.includes(role))
}

export function getProgressPercentage(
  completedSteps: OnboardingStep[],
  role: UserRole
): number {
  const steps = getStepsForRole(role)
  if (steps.length === 0) return 100
  return Math.round((completedSteps.length / steps.length) * 100)
}

export function areHardGateStepsComplete(
  completedSteps: OnboardingStep[],
  role: UserRole
): boolean {
  const hardGateSteps = getStepsForRole(role).filter((s) => s.isHardGate)
  return hardGateSteps.every((step) => completedSteps.includes(step.id))
}

export function areAllStepsComplete(
  completedSteps: OnboardingStep[],
  role: UserRole
): boolean {
  const steps = getStepsForRole(role)
  return steps.every((step) => completedSteps.includes(step.id))
}

export const SECURITY_TRAINING_DEPTH = {
  basic: ['passwords', 'phishing', 'data-handling'],
  advanced: ['incident-response', 'access-control', 'encryption'],
} as const

export const LEGAL_REQUIREMENTS = {
  staff: ['nda', 'employee-handbook', 'code-of-conduct'],
  contractor: ['nda', 'contractor-agreement'],
  manager: ['nda', 'employee-handbook', 'code-of-conduct', 'manager-policy'],
  admin: ['nda', 'employee-handbook', 'code-of-conduct', 'admin-policy'],
} as const

export const SYSTEM_EDUCATION_DEPTH = {
  basic: ['dashboard', 'calendar', 'messaging'],
  advanced: ['reports', 'admin-panel', 'integrations'],
} as const
